/**
 * Officer screens O01–O08, O10, O12 and the R-18 dashboard (children of /app/officer), and the
 * console screens O09, O11, O13 (children of /app/console). Paths are the route map in docs/PLAN.md
 * (M15): server notification links point at them. Every screen loads on demand.
 */
import type { RouteObject } from 'react-router'
import { loadNamespace } from '../../i18n'

/** Each screen's code and the officer strings load together on its first visit. */
function officer<T>(load: () => Promise<T>): () => Promise<T> {
  return async () => (await Promise.all([load(), loadNamespace('officer')]))[0]
}

export const officerRoutes: RouteObject[] = [
  { index: true, lazy: officer(async () => ({ Component: (await import('./screens/HomePage')).HomePage })) },
  {
    path: 'approvals',
    lazy: officer(async () => ({ Component: (await import('./screens/ApprovalsPage')).ApprovalsLayout })),
    children: [
      { index: true, lazy: officer(async () => ({ Component: (await import('./screens/ApprovalsPage')).ApprovalsIndex })) },
      {
        path: ':approvalId',
        lazy: officer(async () => ({ Component: (await import('./screens/ApprovalDetailPage')).ApprovalDetailPage })),
      },
    ],
  },
  { path: 'members', lazy: officer(async () => ({ Component: (await import('./screens/MembersPage')).MembersPage })) },
  {
    path: 'members/:membershipId',
    lazy: officer(async () => ({ Component: (await import('./screens/MemberDetailPage')).MemberDetailPage })),
  },
  { path: 'entries/new', lazy: officer(async () => ({ Component: (await import('./screens/RecordEntryPage')).RecordEntryPage })) },
  { path: 'receipts', lazy: officer(async () => ({ Component: (await import('./screens/ReceiptsPage')).ReceiptsPage })) },
  { path: 'close', lazy: officer(async () => ({ Component: (await import('./screens/ClosePage')).ClosePage })) },
  {
    path: 'close/:closeId',
    lazy: officer(async () => ({ Component: (await import('./screens/CloseDetailPage')).CloseDetailPage })),
  },
  { path: 'rulebook', lazy: officer(async () => ({ Component: (await import('./screens/RulebookPage')).RulebookPage })) },
  { path: 'exits', lazy: officer(async () => ({ Component: (await import('./screens/ExitsPage')).ExitsPage })) },
  { path: 'dashboard', lazy: officer(async () => ({ Component: (await import('./screens/DashboardPage')).DashboardPage })) },
]

export const consoleRoutes: RouteObject[] = [
  {
    path: 'reconciliation',
    lazy: officer(async () => ({ Component: (await import('./console/ReconciliationPage')).ReconciliationPage })),
  },
  { path: 'shareout', lazy: officer(async () => ({ Component: (await import('./console/ShareoutPage')).ShareoutPage })) },
  {
    path: 'shareout/:runId',
    lazy: officer(async () => ({ Component: (await import('./console/ShareoutPage')).ShareoutPage })),
  },
  { path: 'setup', lazy: officer(async () => ({ Component: (await import('./console/SetupPage')).SetupPage })) },
]

/** O13 step 1 needs no office (anyone signed in may start a group), so it sits outside the officer guard. */
export const startGroupRoute: RouteObject = {
  path: 'start-group',
  lazy: officer(async () => ({ Component: (await import('./console/SetupPage')).StartGroupPage })),
}
